import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import FinalCta from "../sections/FinalCta";
import { IMG, JOURNEY_STEPS } from "../data/content";

const REACH = [
  { label: "By Air", text: "The nearest airport is Pantnagar. From there, we escort you by road to the drop-off point at village Bhasedi." },
  { label: "By Rail", text: "Ramnagar railway station is the railhead. From the station, the road to Bhasedi takes around ninety minutes." },
  { label: "By Road", text: "Drive to Ramnagar, then on to Bhasedi. The lodge is about a 90-minute drive from Ramnagar town — then the road ends." },
];

const SEASONS = [
  { label: "September – November", text: "Pleasant. A light jacket helps on early mornings." },
  { label: "December – February", text: "Cold. Full winterwear advised — and the mist is worth it." },
  { label: "March – May", text: "Pleasant again, warming toward summer." },
  { label: "June – August", text: "Hot and humid. The forest rests; so do we." },
];

export default function JourneyPage() {
  return (
    <main data-testid="journey-page">
      <Seo
        title="Getting to The Jamoon | Corbett"
        description="Discover how to reach The Jamoon in Corbett, including the journey through the forest and the final walk to the lodge beside the Ramganga."
        path="/journey"
        image={IMG.heroPoster}
        imageAlt="Forest path on the journey to The Jamoon in Corbett"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Journey", path: "/journey" }]}
      />
      <PageHero
        testid="journey-hero"
        image={IMG.heroPoster}
        imageAlt="Forest path on the journey to The Jamoon in Corbett"
        eyebrow="The Journey"
        title={<>The Journey to <span className="italic text-brass">The Jamoon</span></>}
        sub="Dhikuli to Bhasedi by road — then 3.5 kilometres on foot through the forest."
      />

      <section className="mx-auto max-w-4xl px-6 py-24 sm:px-12 sm:py-32">
        <div className="relative mx-auto max-w-xl">
          <div className="absolute left-4 top-0 h-full w-px bg-gradient-to-b from-brass/60 via-brass/25 to-brass/60 sm:left-1/2" aria-hidden="true" />
          {JOURNEY_STEPS.map((step, i) => (
            <Reveal key={step.name} delay={0.05 * i}>
              <div
                data-testid={`journey-page-step-${i}`}
                className={`relative mb-14 pl-14 last:mb-0 sm:w-1/2 sm:pl-0 ${
                  i % 2 === 0 ? "sm:pr-14 sm:text-right" : "sm:ml-auto sm:pl-14"
                }`}
              >
                <span
                  className={`absolute top-1.5 h-2.5 w-2.5 rounded-full border border-brass bg-charcoal left-[11px] ${
                    i % 2 === 0 ? "sm:-right-[5px] sm:left-auto" : "sm:-left-[5px]"
                  }`}
                  aria-hidden="true"
                />
                <h2 className="font-serif text-2xl font-light tracking-wide text-ivory sm:text-3xl">{step.name}</h2>
                <p className="mt-2 text-sm leading-relaxed text-ivory/55">{step.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-brass/15 bg-forest px-6 py-24 sm:px-12 sm:py-28">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">Getting There</p>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {REACH.map((r, i) => (
              <Reveal key={r.label} delay={i * 0.1}>
                <div data-testid={`reach-card-${i}`} className="border border-brass/20 p-8">
                  <h2 className="font-serif text-2xl font-light text-ivory">{r.label}</h2>
                  <p className="mt-4 text-sm leading-relaxed text-ivory/60">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <p className="mt-16 text-xs font-medium uppercase tracking-[0.3em] text-brass">When to Come</p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 max-w-2xl text-base font-light leading-relaxed text-ivory/75">
              The best time to visit is between October and March — Uttarakhand's summers are hot.
            </p>
          </Reveal>
          <div className="mt-8 grid gap-px border border-ivory/10 bg-ivory/10 sm:grid-cols-2 lg:grid-cols-4">
            {SEASONS.map((s, i) => (
              <div key={s.label} data-testid={`season-card-${i}`} className="bg-forest p-6">
                <p className="text-xs font-medium tracking-[0.2em] text-brass">{s.label}</p>
                <p className="mt-3 text-sm leading-relaxed text-ivory/60">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCta />
    </main>
  );
}
